import { useState, useEffect } from 'react';
import { configApi } from '../services/api';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import {
  Brain,
  Shield,
  ToggleRight,
  Save,
} from 'lucide-react';

interface SystemConfig {
  ai: {
    defaultModel: string;
    maxTokensPerCall: number;
    maxTokensPerDay: number;
    enabledModels: string[];
  };
  limits: {
    maxWorkflowsPerUser: number;
    maxFileSize: number;
    maxExecutionTime: number;
    maxNodesPerWorkflow: number;
  };
  features: {
    enableAI: boolean;
    enableTeams: boolean;
    enableTemplates: boolean;
    enableAPI: boolean;
  };
}

export function ConfigPage() {
  const [config, setConfig] = useState<SystemConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'ai' | 'limits' | 'features'>('ai');

  const fetchConfig = async () => {
    setIsLoading(true);
    try {
      const response = await configApi.getConfig();
      if (response.code === 0) {
        setConfig(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch config:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleSave = async () => {
    if (!config) return;
    setIsSaving(true);
    try {
      await configApi.updateConfig(config);
      alert('配置已保存');
    } catch (err) {
      console.error('Failed to save config:', err);
      alert('保存失败');
    } finally {
      setIsSaving(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="text-center py-12">
        <p className="text-zinc-500">加载配置失败</p>
        <Button onClick={fetchConfig} className="mt-4">
          重试
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">系统配置</h1>
          <p className="text-zinc-500 mt-1">管理系统的各项配置</p>
        </div>
        <Button onClick={handleSave} isLoading={isSaving}>
          <Save className="w-4 h-4" />
          保存配置
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b border-zinc-200">
        <nav className="flex gap-6">
          {[
            { key: 'ai', label: 'AI 配置', icon: Brain },
            { key: 'limits', label: '系统限制', icon: Shield },
            { key: 'features', label: '功能开关', icon: ToggleRight },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as typeof activeTab)}
              className={`flex items-center gap-2 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-accent-600 text-accent-600'
                  : 'border-transparent text-zinc-500 hover:text-zinc-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* AI Config */}
      {activeTab === 'ai' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-accent-600" />
              AI 模型配置
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                默认模型
              </label>
              <select
                value={config.ai.defaultModel}
                onChange={(e) => setConfig({
                  ...config,
                  ai: { ...config.ai, defaultModel: e.target.value }
                })}
                className="w-full px-3 py-2 rounded-lg border border-zinc-300"
              >
                {config.ai.enabledModels.map((model) => (
                  <option key={model} value={model}>{model}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                单次调用最大 Token 数
              </label>
              <input
                type="number"
                value={config.ai.maxTokensPerCall}
                onChange={(e) => setConfig({
                  ...config,
                  ai: { ...config.ai, maxTokensPerCall: parseInt(e.target.value) }
                })}
                className="w-full px-3 py-2 rounded-lg border border-zinc-300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                每日最大 Token 数
              </label>
              <input
                type="number"
                value={config.ai.maxTokensPerDay}
                onChange={(e) => setConfig({
                  ...config,
                  ai: { ...config.ai, maxTokensPerDay: parseInt(e.target.value) }
                })}
                className="w-full px-3 py-2 rounded-lg border border-zinc-300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                启用的模型
              </label>
              <div className="space-y-2">
                {['gpt-4', 'gpt-4o', 'claude-3', 'claude-3.5'].map((model) => (
                  <label key={model} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={config.ai.enabledModels.includes(model)}
                      onChange={(e) => {
                        const newModels = e.target.checked
                          ? [...config.ai.enabledModels, model]
                          : config.ai.enabledModels.filter(m => m !== model);
                        setConfig({
                          ...config,
                          ai: { ...config.ai, enabledModels: newModels }
                        });
                      }}
                      className="rounded border-zinc-300"
                    />
                    <span className="text-sm">{model}</span>
                  </label>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Limits Config */}
      {activeTab === 'limits' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-accent-600" />
              系统限制
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                每个用户最大工作流数量
              </label>
              <input
                type="number"
                value={config.limits.maxWorkflowsPerUser}
                onChange={(e) => setConfig({
                  ...config,
                  limits: { ...config.limits, maxWorkflowsPerUser: parseInt(e.target.value) }
                })}
                className="w-full px-3 py-2 rounded-lg border border-zinc-300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                最大文件大小 (MB)
              </label>
              <input
                type="number"
                value={Math.round(config.limits.maxFileSize / 1024 / 1024)}
                onChange={(e) => setConfig({
                  ...config,
                  limits: { ...config.limits, maxFileSize: parseInt(e.target.value) * 1024 * 1024 }
                })}
                className="w-full px-3 py-2 rounded-lg border border-zinc-300"
              />
              <p className="text-xs text-zinc-500 mt-1">
                当前: {formatFileSize(config.limits.maxFileSize)}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                最大执行时间 (秒)
              </label>
              <input
                type="number"
                value={config.limits.maxExecutionTime}
                onChange={(e) => setConfig({
                  ...config,
                  limits: { ...config.limits, maxExecutionTime: parseInt(e.target.value) }
                })}
                className="w-full px-3 py-2 rounded-lg border border-zinc-300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                每个工作流最大节点数
              </label>
              <input
                type="number"
                value={config.limits.maxNodesPerWorkflow}
                onChange={(e) => setConfig({
                  ...config,
                  limits: { ...config.limits, maxNodesPerWorkflow: parseInt(e.target.value) }
                })}
                className="w-full px-3 py-2 rounded-lg border border-zinc-300"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Features Config */}
      {activeTab === 'features' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ToggleRight className="w-5 h-5 text-accent-600" />
              功能开关
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-lg">
              <div>
                <p className="font-medium text-zinc-900">AI 功能</p>
                <p className="text-sm text-zinc-500">启用 AI 处理和智能功能</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.features.enableAI}
                  onChange={(e) => setConfig({
                    ...config,
                    features: { ...config.features, enableAI: e.target.checked }
                  })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-600"></div>
              </label>
            </div>
            <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-lg">
              <div>
                <p className="font-medium text-zinc-900">团队功能</p>
                <p className="text-sm text-zinc-500">启用团队协作功能</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.features.enableTeams}
                  onChange={(e) => setConfig({
                    ...config,
                    features: { ...config.features, enableTeams: e.target.checked }
                  })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-600"></div>
              </label>
            </div>
            <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-lg">
              <div>
                <p className="font-medium text-zinc-900">模板市场</p>
                <p className="text-sm text-zinc-500">启用模板分享和下载功能</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.features.enableTemplates}
                  onChange={(e) => setConfig({
                    ...config,
                    features: { ...config.features, enableTemplates: e.target.checked }
                  })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-600"></div>
              </label>
            </div>
            <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-lg">
              <div>
                <p className="font-medium text-zinc-900">API 功能</p>
                <p className="text-sm text-zinc-500">启用 API 和 Webhook 功能</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.features.enableAPI}
                  onChange={(e) => setConfig({
                    ...config,
                    features: { ...config.features, enableAPI: e.target.checked }
                  })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-600"></div>
              </label>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
